/**
 * <AbsorptionArc /> — quiet 21-day proof that absorption is sharpening.
 *
 * Reads each day's familiarity map from localStorage (written by
 * <EmbeddedQuiz>) and renders a calm cool-blue SVG line of the
 * familiar:total ratio across the journey. No numbers, no axis labels,
 * no judgement — just the felt-sense of an upward curve.
 *
 * Surfaced as an optional collapsible section on JourneyDetailPage.
 * Hidden by default (zero pressure); user taps to reveal.
 */
import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { JOURNEY_PALETTE as P, JOURNEY_FONT_HEAD, JOURNEY_FONT_LABEL } from "./journeyTheme";
import { loadFamiliarity } from "./familiarityStore";

export default function AbsorptionArc({ slug, totalDays = 21 }) {
  const [open, setOpen] = useState(false);

  // Walk Days 1..totalDays, compute familiar:total ratio per day.
  // Days the user hasn't quizzed yet → ratio=null (skip in the line).
  const data = useMemo(() => {
    const points = [];
    for (let n = 1; n <= totalDays; n++) {
      const m = loadFamiliarity(slug, n);
      if (!m) {
        points.push({ day: n, ratio: null });
        continue;
      }
      const total = (m.familiar?.length || 0) + (m.unfamiliar?.length || 0);
      points.push({
        day: n,
        ratio: total > 0 ? (m.familiar.length / total) : null,
      });
    }
    return points;
  }, [slug, totalDays]);

  const sampledCount = data.filter((p) => p.ratio !== null).length;

  // Detect whether the user has crossed any of the milestone days.
  // Used to pulse the arc card softly with gold once.
  // (Computed UNCONDITIONALLY — must run on every render per Hooks rules,
  // even when we'll early-return below.)
  const milestoneReached = useMemo(() => {
    return [7, 14, 21].some((m) => {
      const lastSampled = data.reduceRight(
        (acc, p, i) => acc === -1 && p.ratio !== null ? i + 1 : acc,
        -1
      );
      return lastSampled >= m;
    });
  }, [data]);

  // Only render the arc panel if the user has at least 1 quiz on record —
  // otherwise the surface would be empty and confusing.
  if (sampledCount === 0) return null;

  // Build the SVG path. Width and height are responsive via viewBox.
  const VIEW_W = 320;
  const VIEW_H = 80;
  const PAD_X = 8;
  const stepX = (VIEW_W - PAD_X * 2) / (totalDays - 1);

  // Filter to only sampled points for the line; we still render baseline
  // dots for unsampled days as faint hints.
  const sampledPath = (() => {
    const pts = data
      .map((p, i) => ({ i, ratio: p.ratio }))
      .filter((p) => p.ratio !== null);
    if (pts.length === 0) return "";
    if (pts.length === 1) {
      const x = PAD_X + pts[0].i * stepX;
      const y = VIEW_H - 10 - pts[0].ratio * (VIEW_H - 20);
      return `M ${x} ${y}`;
    }
    // Smooth quadratic curve through the points
    const xy = pts.map((p) => ({
      x: PAD_X + p.i * stepX,
      y: VIEW_H - 10 - p.ratio * (VIEW_H - 20),
    }));
    let d = `M ${xy[0].x.toFixed(1)} ${xy[0].y.toFixed(1)}`;
    for (let i = 1; i < xy.length; i++) {
      const mx = (xy[i - 1].x + xy[i].x) / 2;
      const my = (xy[i - 1].y + xy[i].y) / 2;
      d += ` Q ${xy[i - 1].x.toFixed(1)} ${xy[i - 1].y.toFixed(1)} ${mx.toFixed(1)} ${my.toFixed(1)}`;
    }
    const last = xy[xy.length - 1];
    d += ` T ${last.x.toFixed(1)} ${last.y.toFixed(1)}`;
    return d;
  })();

  // Trend hint — first sampled vs last sampled, no numbers exposed.
  const trendHint = (() => {
    const sampled = data.filter((p) => p.ratio !== null);
    if (sampled.length < 2) return "Settling in.";
    const first = sampled[0].ratio;
    const last = sampled[sampled.length - 1].ratio;
    if (last > first + 0.08) return "Something is settling in.";
    if (last < first - 0.12) return "Some days are softer than others — keep going.";
    return "Quietly steady.";
  })();

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
      className="mt-6 rounded-[20px] overflow-hidden relative"
      style={{
        background: "rgba(255,255,255,0.04)",
        backdropFilter: "blur(18px) saturate(1.1)",
        WebkitBackdropFilter: "blur(18px) saturate(1.1)",
        border: milestoneReached
          ? "1px solid rgba(212,184,126,0.30)"
          : "1px solid rgba(255,255,255,0.08)",
        boxShadow: milestoneReached
          ? "0 0 28px rgba(212,184,126,0.18)"
          : "none",
      }}
      data-testid="absorption-arc"
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between p-5"
        data-testid="absorption-arc-toggle"
      >
        <div className="text-left">
          <p
            className="text-[10px] uppercase tracking-[0.4em] mb-1"
            style={{ color: P.accentBlue, fontFamily: JOURNEY_FONT_LABEL, fontWeight: 500 }}
          >
            21-day arc
          </p>
          <p
            className="text-[14px]"
            style={{ color: "rgba(255,255,255,0.92)", fontFamily: JOURNEY_FONT_HEAD }}
          >
            How your absorption is settling
          </p>
        </div>
        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          <ChevronDown size={18} style={{ color: P.accentBlue }} />
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="arc-content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-6">
              <svg
                viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
                className="w-full h-auto"
                preserveAspectRatio="none"
                data-testid="absorption-arc-svg"
              >
                <defs>
                  <linearGradient id="arc-line" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%"   stopColor="rgba(77,163,255,0.55)" />
                    <stop offset="100%" stopColor="rgba(77,163,255,1.0)" />
                  </linearGradient>
                  <linearGradient id="arc-fill" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%"   stopColor="rgba(77,163,255,0.18)" />
                    <stop offset="100%" stopColor="rgba(77,163,255,0)" />
                  </linearGradient>
                </defs>

                {/* Hairline baseline */}
                <line
                  x1={PAD_X}
                  x2={VIEW_W - PAD_X}
                  y1={VIEW_H - 10}
                  y2={VIEW_H - 10}
                  stroke="rgba(255,255,255,0.10)"
                  strokeWidth="0.7"
                />

                {/* Faint baseline dots for every day */}
                {data.map((p, i) => (
                  <circle
                    key={`dot-${i}`}
                    cx={PAD_X + i * stepX}
                    cy={VIEW_H - 10}
                    r={1.4}
                    fill={
                      p.ratio === null
                        ? "rgba(255,255,255,0.18)"
                        : "rgba(77,163,255,0.55)"
                    }
                  />
                ))}

                {/* Filled area under the line */}
                {sampledPath && (
                  <path
                    d={`${sampledPath} L ${VIEW_W - PAD_X} ${VIEW_H - 10} L ${PAD_X} ${VIEW_H - 10} Z`}
                    fill="url(#arc-fill)"
                  />
                )}

                {/* The arc line itself */}
                {sampledPath && (
                  <path
                    d={sampledPath}
                    fill="none"
                    stroke="url(#arc-line)"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                )}

                {/* Sampled-day glow dots on top of the line */}
                {data.map((p, i) =>
                  p.ratio === null ? null : (
                    <circle
                      key={`samp-${i}`}
                      cx={PAD_X + i * stepX}
                      cy={VIEW_H - 10 - p.ratio * (VIEW_H - 20)}
                      r={2.2}
                      fill="#FFFFFF"
                      stroke="rgba(77,163,255,0.85)"
                      strokeWidth="1.4"
                    />
                  )
                )}
              </svg>

              <p
                className="italic mt-4 text-[13px] leading-snug"
                style={{
                  color: "rgba(255,255,255,0.78)",
                  fontFamily: JOURNEY_FONT_HEAD,
                  fontWeight: 300,
                }}
                data-testid="absorption-arc-hint"
              >
                {trendHint}
              </p>

              <div className="flex items-center justify-between mt-3">
                <span
                  className="text-[10px] uppercase tracking-[0.32em]"
                  style={{ color: "rgba(255,255,255,0.45)", fontFamily: JOURNEY_FONT_LABEL }}
                >
                  Day 1
                </span>
                <span
                  className="text-[10px] uppercase tracking-[0.32em]"
                  style={{ color: "rgba(255,255,255,0.45)", fontFamily: JOURNEY_FONT_LABEL }}
                >
                  Day {totalDays}
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
